import React from 'react';
import { Button, CircularProgress, FormControl, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { gridSizeMdLg12, gridSizeMdLg6, toHour } from '@/shared';
import dayjs from 'dayjs';
import { Refresh } from '@mui/icons-material';
import { texto } from '@/shared/utils';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CustomConfirmDialogProps } from '@/shared/components/ui/Dialogs';
import useSelectDateTimerPreScheduling from '@/erp/sale/component/calendario-preagendamiento-venta/useSelectDateTimerPreScheduling.store';
import { CustomAutocompleteNoForm } from '@/shared/components/ui';
import { Flota } from '@/shared/interfaces';

const fmt = (ms: number) => {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * Props for DateTimeSelector component.
 * @param value - Fecha y hora inicial (opcional).
 * @param onChange - Callback cuando cambia la fecha u hora.
 */
interface DateTimeSelectorProps {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  /**
   * Texto del diálogo de confirmación al elegir la hora. Opcional: por defecto
   * se mantiene el mensaje de 10 minutos que usan las demás pantallas.
   */
  mensajeBloqueo?: string;
}

export const SelectDateTimerSchedulingPyl:
  React.FC<DateTimeSelectorProps> = ({
    mensajeBloqueo,
  }) => {

    const float = useSelectDateTimerPreScheduling(s => s.float);
    const selectFloat = useSelectDateTimerPreScheduling(s => s.selectFloat);
    const isLoadingFloat = useSelectDateTimerPreScheduling(s => s.isLoadingFloat);
    const remainingMs = useSelectDateTimerPreScheduling(s => s.remainingMs);
    const isLoadingPlanner = useSelectDateTimerPreScheduling(s => s.isLoadingPlanner);
    const modalConfirmBlocked = useSelectDateTimerPreScheduling(s => s.modalConfirmBlocked);
    const blocked = useSelectDateTimerPreScheduling(s => s.blocked);
    const planner = useSelectDateTimerPreScheduling(s => s.planner);
    const setPlanner = useSelectDateTimerPreScheduling(s => s.setPlanner);
    const selectDate = useSelectDateTimerPreScheduling(s => s.selectDate);
    const setSelectDate = useSelectDateTimerPreScheduling(s => s.setSelectDate);
    const handleVerificationPlanner = useSelectDateTimerPreScheduling(s => s.handleVerificationPlanner);
    const handleRefreshPlanner = useSelectDateTimerPreScheduling(s => s.handleRefreshPlanner);
    const handleConfirmedPlanner = useSelectDateTimerPreScheduling(s => s.handleConfirmedPlanner);
    const handleCloseModalConfirmBlocked = useSelectDateTimerPreScheduling(s => s.handleCloseModalConfirmBlocked);
    const mostrarMensajeBloqueoSoftnetPorDia = useSelectDateTimerPreScheduling(s => s.mostrarMensajeBloqueoSoftnetPorDia);
    const autoMessageKeyRef = React.useRef<string | null>(null);

    React.useEffect(() => {
      if (!selectFloat || !selectDate?.isValid()) return;

      const key = `${selectFloat}-${selectDate.format('YYYY-MM-DD')}`;
      if (autoMessageKeyRef.current === key) return;

      autoMessageKeyRef.current = key;
      mostrarMensajeBloqueoSoftnetPorDia(selectDate, selectFloat);
    }, [selectFloat]);

    return (
      <>
        <Grid container spacing={3}>
          {
            blocked && (
              <Grid item xs={12}>
                <Typography variant='subtitle1'
                  style={{
                    textAlign: 'center'
                  }} >
                  Planificador bloqueado por {fmt(remainingMs)} min
                </Typography>

              </Grid>
            )
          }
          <Grid item xs={12}>

            <Grid container
              spacing={3}
              justifyContent={'space-between'}
              alignContent={'space-between'}
              sx={{
                pointerEvents: blocked ? 'none' : 'auto',
                borderRadius: 1,
                //p: 0.5,
                opacity: blocked ? 0.5 : '1',
                //backgroundColor: blocked ? '#e0e0e0ff' : 'inherit'
              }}
            >

              <CustomAutocompleteNoForm<Flota>
                size={gridSizeMdLg6}
                label={'Técnico disponibles'}
                options={float || []}
                valueKey="nombre"
                actualValueKey="codigo"
                defaultValue={selectFloat || ""}
                value={selectFloat || ""}
                isLoadingData={isLoadingFloat}
                onChangeValue={(e) => 
                  setPlanner(e || "")
                }
              />

              <Grid item {...gridSizeMdLg6}>
                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                  >
                    <DatePicker
                      format='YYYY-MM-DD'
                      label="Seleccionar fecha"
                      minDate={dayjs(new Date())}
                      referenceDate={dayjs(new Date())}
                      value={selectDate}
                      onChange={e => {
                        const selectedDate = dayjs(e);
                        setSelectDate(selectedDate);
                      }}
                      onAccept={e => {
                        const selectedDate = dayjs(e);
                        mostrarMensajeBloqueoSoftnetPorDia(selectedDate);
                      }}
                    />
                  </LocalizationProvider>
                </FormControl>

              </Grid>

              <Grid {...gridSizeMdLg6} item >

                <img
                  src="/calendar-planificador.svg"
                  alt="select hour"
                  draggable="false"
                  style={{
                    width: '100%',
                    height: 'auto',
                    margin: 'auto',
                    objectFit: 'cover',
                  }}
                />

              </Grid>
              <Grid {...gridSizeMdLg6} item container spacing={0}>

                <Grid item xs={10}>
                  <Typography variant="subtitle1" sx={{ textAlign: 'start' }}>
                    Horas disponibles
                  </Typography>
                </Grid>


                <Grid item xs={2}>
                  <Tooltip title="Actualizar Horas" placement="top">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        handleRefreshPlanner()
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Grid>

                <Grid item {...gridSizeMdLg12} 
                justifyContent="center" 
                alignItems="center">

                  {
                    !isLoadingPlanner && planner ? (
                      <>
                        {
                          planner.horas.map((element, index) => {
                            return <Button
                              sx={{
                                margin: 0.3
                              }}
                              variant='contained'
                              key={`${element.hora}-${index}`}
                              disabled={element.estado}
                              color={element.estado
                                ? 'warning'
                                : 'primary'
                              }
                              onClick={() => {
                                handleVerificationPlanner(element)
                              }}
                            >
                              {
                                toHour(element.hora)
                              }
                            </Button>

                          })
                        }
                      </>
                    ) : (
                      <CircularProgress />
                    )
                  }

                </Grid>
              </Grid>
            </Grid>

          </Grid>
        </Grid>

        {
          <CustomConfirmDialogProps
            open={modalConfirmBlocked}
            title="Seleccionar horario de instalación"
            subtitle="¿Está seguro de querer agendar la instalacion en este horario?"
            text2={mensajeBloqueo ?? texto.mensaje_bloquar_horario_10_min}
            confirmTextBtn="Si, seleccionar horario"
            onClose={() =>
              handleCloseModalConfirmBlocked()
            }
            onConfirm={() =>
              handleConfirmedPlanner()
            }
          />
        }
      </>
    );
  };
